import * as pako from 'pako';
import * as UTIF from 'utif';

export interface ImageConversionOptions {
  compress?: boolean;
  quality?: number;
  maxWidth?: number;
  maxHeight?: number;
}

/**
 * Convert various image formats to displayable format
 */
export class ImageConverter {
  /**
   * Decompress image data based on compression type
   */
  static decompressImage(
    content: any,
    compressionType?: any
  ): Uint8Array {
    try {
      // Handle different content formats
      let bufferData: Uint8Array;

      if (content instanceof Uint8Array) {
        bufferData = content;
      } else if (content.data && Array.isArray(content.data)) {
        bufferData = new Uint8Array(content.data);
      } else if (typeof content === 'string') {
        // If it's a base64 string, decode it
        const base64Data = content.replace(/^data:image\/[a-z]+;base64,/, '');
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        bufferData = bytes;
      } else if (Buffer.isBuffer(content)) {
        bufferData = new Uint8Array(content);
      } else {
        bufferData = new Uint8Array(content);
      }

      // Decompress based on compression type
      let decompressedData: Uint8Array;

      // Convert compressionType to string if needed
      const compType = compressionType ? String(compressionType).toLowerCase() : '';

      if (compType === 'gzip' || compType === '1') {
        decompressedData = pako.ungzip(bufferData);
      } else if (compType === 'deflate' || compType === '2' || compressionType) {
        // Default to inflate if any compression type is specified
        decompressedData = pako.inflate(bufferData);
      } else {
        // No compression or unknown type
        decompressedData = bufferData;
      }

      return decompressedData;
    } catch (error) {
      console.error('Failed to decompress image:', error);
      // Return original data if decompression fails
      if (content instanceof Uint8Array) {
        return content;
      }
      return new Uint8Array(content);
    }
  }

  /**
   * Convert binary data to base64 string
   */
  static toBase64(data: Uint8Array): string {
    let binary = '';
    const bytes = new Uint8Array(data);
    const len = bytes.byteLength;

    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }

    return btoa(binary);
  }

  /**
   * Create a data URL from binary data
   */
  static createDataUrl(
    data: Uint8Array | string,
    mimeType: string
  ): string {
    if (typeof data === 'string') {
      // Already base64
      return `data:${mimeType};base64,${data}`;
    }

    const base64 = this.toBase64(data);
    return `data:${mimeType};base64,${base64}`;
  }

  /**
   * Create a blob URL from binary data
   */
  static createBlobUrl(
    data: Uint8Array | string,
    mimeType: string
  ): string {
    let bytes: Uint8Array;

    if (typeof data === 'string') {
      // Decode base64
      const base64Data = data.replace(/^data:image\/[a-z]+;base64,/, '');
      const binaryString = atob(base64Data);
      bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
    } else {
      bytes = data;
    }

    const blob = new Blob([bytes.buffer as ArrayBuffer], { type: mimeType });
    return URL.createObjectURL(blob);
  }

  /**
   * Process and prepare image for display
   */
  static async prepareImageForDisplay(
    content: any,
    mimeType: string,
    compressionType?: string
  ): Promise<{
    dataUrl: string;
    blobUrl: string;
    base64: string;
    tiffUrls?: string[];
  }> {
    try {
      console.log('prepareImageForDisplay - Input:', {
        contentType: typeof content,
        mimeType,
        compressionType,
        contentLength: content?.length || content?.data?.length
      });

      // First, handle the content format
      let rawData: Uint8Array;

      if (typeof content === 'string') {
        // Content is already base64 string from backend
        console.log('Content is base64 string');
        const base64Data = content.replace(/^data:image\/[a-z]+;base64,/, '');

        // Check if it's a TIFF image
        let tiffUrls: string[] | undefined;
        if (mimeType?.toLowerCase().includes('tiff') || mimeType?.toLowerCase().includes('tif')) {
          try {
            // Convert base64 to Uint8Array for TIFF processing
            const binaryString = atob(base64Data);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            tiffUrls = await this.convertTiffToCanvas(bytes);
            console.log('TIFF conversion successful, pages:', tiffUrls.length);
          } catch (error) {
            console.error('Failed to convert TIFF:', error);
          }
        }

        // For base64 content, create proper data URL
        const dataUrl = `data:${mimeType};base64,${base64Data}`;
        const blobUrl = this.createBlobUrl(base64Data, mimeType);

        return {
          dataUrl,
          blobUrl,
          base64: base64Data,
          tiffUrls,
        };
      } else {
        // Content needs decompression or conversion
        if (content.data && Array.isArray(content.data)) {
          rawData = new Uint8Array(content.data);
        } else if (content instanceof Uint8Array) {
          rawData = content;
        } else if (Buffer.isBuffer(content)) {
          rawData = new Uint8Array(content);
        } else {
          rawData = new Uint8Array(content);
        }

        // Decompress if needed
        const decompressedData = compressionType
          ? this.decompressImage(rawData, compressionType)
          : rawData;

        console.log('Decompressed data length:', decompressedData.length);

        // Convert to base64
        const base64 = this.toBase64(decompressedData);

        // Check if it's a TIFF image
        let tiffUrls: string[] | undefined;
        if (mimeType?.toLowerCase().includes('tiff') || mimeType?.toLowerCase().includes('tif')) {
          try {
            tiffUrls = await this.convertTiffToCanvas(decompressedData);
            console.log('TIFF conversion successful, pages:', tiffUrls.length);
          } catch (error) {
            console.error('Failed to convert TIFF:', error);
          }
        }

        // Create URLs
        const dataUrl = this.createDataUrl(base64, mimeType);
        const blobUrl = this.createBlobUrl(base64, mimeType);

        return {
          dataUrl,
          blobUrl,
          base64,
          tiffUrls,
        };
      }
    } catch (error) {
      console.error('Failed to prepare image for display:', error);
      throw error;
    }
  }

  /**
   * Detect image format from binary data
   */
  static detectImageFormat(data: Uint8Array): string {
    if (!data || data.length < 4) {
      return 'application/octet-stream';
    }

    // Check magic numbers
    const header = Array.from(data.slice(0, 4))
      .map(byte => byte.toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase();

    // Common image format signatures
    if (header.startsWith('FFD8FF')) {
      return 'image/jpeg';
    } else if (header.startsWith('89504E47')) {
      return 'image/png';
    } else if (header.startsWith('47494638')) {
      return 'image/gif';
    } else if (header.startsWith('424D')) {
      return 'image/bmp';
    } else if (header.startsWith('49492A00') || header.startsWith('4D4D002A')) {
      return 'image/tiff';
    } else if (header.startsWith('25504446')) {
      return 'application/pdf';
    }

    return 'application/octet-stream';
  }

  /**
   * Sniff the true MIME type from the first bytes of base64-encoded content.
   *
   * Kroll's `ScriptImageContent.ContentMimeType` is the label we normally trust,
   * but it is sometimes missing or wrong for generated documents — notably BC
   * adaptation / emergency-supply prescriptions, which land on the Burnaby Kroll
   * as PDFs with no (or a non-PDF) MIME. A mislabeled PDF then routes to an
   * `<img>` and the script "never shows" on Product / Clinical Verification
   * (GP-1015). Detecting from the actual bytes lets callers route it correctly.
   *
   * Only the header is decoded (9 bytes), so this stays cheap on multi-MB scans.
   * Returns null when the format can't be positively identified — callers then
   * keep whatever MIME Kroll declared (no fabrication).
   */
  static detectMimeTypeFromBase64(base64?: string | null): string | null {
    if (!base64) return null;
    try {
      const cleaned = base64.replace(/^data:[^;]+;base64,/, '');
      // 12 base64 chars → 9 decoded bytes: enough for every magic number checked
      // in detectImageFormat, and a multiple of 4 so atob decodes the slice cleanly.
      const header = cleaned.slice(0, 12);
      if (header.length < 4) return null;
      const binary = atob(header);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      const detected = this.detectImageFormat(bytes);
      return detected === 'application/octet-stream' ? null : detected;
    } catch {
      return null;
    }
  }

  /**
   * Convert TIFF image to displayable format using UTIF
   */
  static async convertTiffToCanvas(data: Uint8Array): Promise<string[]> {
    try {
      // Validate input data
      if (!data || data.length === 0) {
        console.warn('TIFF conversion skipped: empty data');
        return [];
      }

      // Decode TIFF using UTIF
      let ifds;
      try {
        ifds = UTIF.decode(data.buffer as ArrayBuffer);
      } catch (decodeError) {
        console.warn('TIFF decode failed - file may be corrupted:', decodeError);
        return []; // Return empty array instead of throwing
      }

      if (!ifds || ifds.length === 0) {
        console.warn('TIFF decode successful but no pages found');
        return [];
      }

      const imageUrls: string[] = [];

      for (let i = 0; i < ifds.length; i++) {
        const ifd = ifds[i];

        try {
          // Decode the image data
          UTIF.decodeImage(data.buffer as ArrayBuffer, ifd);

          // Get image dimensions with validation
          const width = ifd.width || ifd.t256?.[0] || 0;
          const height = ifd.height || ifd.t257?.[0] || 0;

          if (width === 0 || height === 0) {
            console.warn(`TIFF page ${i} skipped: invalid dimensions (${width}x${height})`);
            continue;
          }

          // Validate dimensions are reasonable (not too large)
          if (width > 10000 || height > 10000) {
            console.warn(`TIFF page ${i} skipped: dimensions too large (${width}x${height})`);
            continue;
          }

          // Create canvas and convert TIFF data to RGBA
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');

          if (!ctx) {
            console.warn(`TIFF page ${i} skipped: failed to get canvas context`);
            continue;
          }

          // Convert TIFF data to RGBA format
          const rgba = UTIF.toRGBA8(ifd);

          if (!rgba || rgba.length === 0) {
            console.warn(`TIFF page ${i} skipped: RGBA conversion returned no data`);
            continue;
          }

          // Validate RGBA data length
          const expectedLength = width * height * 4; // RGBA = 4 bytes per pixel
          if (rgba.length !== expectedLength) {
            console.warn(`TIFF page ${i} skipped: RGBA data length mismatch (expected ${expectedLength}, got ${rgba.length})`);
            continue;
          }

          // Create ImageData from RGBA array
          const imageData = ctx.createImageData(width, height);

          // Copy RGBA data to ImageData
          for (let j = 0; j < rgba.length; j++) {
            imageData.data[j] = rgba[j];
          }

          // Put image data on canvas
          ctx.putImageData(imageData, 0, 0);

          // Convert canvas to data URL
          imageUrls.push(canvas.toDataURL('image/png'));
        } catch (pageError) {
          console.warn(`TIFF page ${i} skipped due to error:`, pageError);
          continue; // Skip this page and continue with others
        }
      }

      if (imageUrls.length === 0) {
        console.warn(`TIFF conversion completed but no valid pages extracted from ${ifds.length} total page(s)`);
      }

      return imageUrls;
    } catch (error) {
      console.error('TIFF conversion failed completely:', error);
      // Return empty array instead of throwing - let caller handle fallback
      return [];
    }
  }

  /**
   * Check if MIME type is supported for display
   */
  static isSupportedImageType(mimeType: string): boolean {
    const supportedTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/bmp',
      'image/webp',
      'image/svg+xml',
      'image/tiff',
      'image/tif',
    ];

    return supportedTypes.includes(mimeType.toLowerCase());
  }

  /**
   * Get file extension from MIME type
   */
  static getFileExtension(mimeType: string): string {
    const mimeToExt: Record<string, string> = {
      'image/jpeg': '.jpg',
      'image/jpg': '.jpg',
      'image/png': '.png',
      'image/gif': '.gif',
      'image/bmp': '.bmp',
      'image/webp': '.webp',
      'image/svg+xml': '.svg',
      'image/tiff': '.tiff',
      'application/pdf': '.pdf',
    };

    return mimeToExt[mimeType.toLowerCase()] || '.bin';
  }
}